import { createError } from '@common/neo.tax';
import { Stack } from '@ui-components/neo.tax';
import React, { useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import { AscContextProvider } from '@neo.tax/asc/context';
import {
  getFiscalYearSubsetPeriod,
  ProductType,
  useFiscalYearOrFail,
  useSelectedProductOptional,
  useSelectedFiscalYearIdOptional,
  useSelectedPeriodNameOptional,
} from '@neo.tax/shared';

import { AscSideNav } from '../asc-side-nav';
import { Container } from './asc-home-layout.styled';

export const AscHomeLayout = (): React.ReactElement | null => {
  const product = useSelectedProductOptional();
  const periodName = useSelectedPeriodNameOptional();
  const fiscalYearId = useSelectedFiscalYearIdOptional();
  if (product !== ProductType.UsAsc || !periodName || !fiscalYearId) {
    throw createError('AscHomeLayoutError', 'Product is not ASC or period name or fiscal year id not found');
  }

  const { fiscalYear } = useFiscalYearOrFail({ fiscalYearId });
  // confirm that the fiscal year product type is ASC
  if (fiscalYear.productType !== ProductType.UsAsc || !fiscalYear.enabledDatetime) {
    throw createError('AscHomeLayoutError', 'Fiscal year product type is not ASC or is not enabled');
  }

  const period = useMemo(
    () => getFiscalYearSubsetPeriod(fiscalYear.taxYearStartDate, fiscalYear.taxYearEndDate, periodName),
    [fiscalYear.taxYearStartDate, fiscalYear.taxYearEndDate, periodName],
  );

  return (
    <AscContextProvider
      fiscalYearId={fiscalYearId}
      fiscalYear={fiscalYear}
      periodName={periodName}
      period={period}
      product={product}
    >
      <Container>
        <AscSideNav />
        <Stack fullHeight fullWidth overflow="y-auto">
          <Outlet />
        </Stack>
      </Container>
    </AscContextProvider>
  );
};
